import type { PanelArtifact } from '../context/ArtifactsPanelContext';

/**
 * Frontend-only intent detection for "show me X" chat messages.
 *
 * When the user types something like "נתח שמאות" while a project is active,
 * we want to auto-open the matching artifact in the side panel — they
 * shouldn't have to also click the chip button in the project header.
 *
 * This is a stop-gap until the backend tags chat responses with structured
 * `artifact_ref` metadata. The downside of frontend matching is that it
 * fires *before* the AI answers, so the panel may show stale data if the
 * AI's reply is actually creating a new valuation. The upside is zero
 * backend changes; flag this off easily via the empty-pattern list.
 *
 * Patterns are deliberately loose — Hebrew and English, with/without
 * leading verbs, no full sentence parsing. False positives are cheap (just
 * a panel open the user can close); false negatives are costly (the magic
 * doesn't work). We err on the recall side.
 */

/** Loose substring matching. Lowercased and trimmed for English; Hebrew kept as-is. */
const VALUATION_PATTERNS: RegExp[] = [
  /שמאות/, // generic "appraisal/valuation"
  /הערכת שווי/, // "value assessment"
  /\bvaluation\b/i,
  /\bappraisal\b/i,
];

const UNDERWRITING_PATTERNS: RegExp[] = [
  /חיתום/,
  /הקשר חיתום/,
  /\bunderwriting\b/i,
];

const GRAPH_PATTERNS: RegExp[] = [
  /גרף ידע/,
  /גרף מסמכים/,
  /\bknowledge graph\b/i,
  /\bgraph view\b/i,
];

/**
 * Inspect the user's chat message and return the artifact to open, or null
 * if nothing matches.
 *
 * @param message  The raw user message text.
 * @param projectId  The currently-active project id (or null). All
 *                   artifact kinds require a project, so returning null
 *                   when there's no project is correct.
 */
export function detectArtifactIntent(
  message: string,
  projectId: string | null | undefined,
): PanelArtifact | null {
  if (!projectId) return null;
  const trimmed = message.trim();
  if (!trimmed) return null;

  if (VALUATION_PATTERNS.some((re) => re.test(trimmed))) {
    return { kind: 'valuation', projectId };
  }
  if (UNDERWRITING_PATTERNS.some((re) => re.test(trimmed))) {
    return { kind: 'underwriting', projectId };
  }
  if (GRAPH_PATTERNS.some((re) => re.test(trimmed))) {
    return { kind: 'knowledge_graph', projectId };
  }
  return null;
}
